import { EntityType, ReferenceDataEntityType } from 'api'
import { EntityTypeFor } from 'model'
import { useState } from 'react'
import {
  createWorkflowReferenceDataAvailableEventsSelector,
  createWorkflowReferenceDataEntitiesSelector,
  createWorkflowReferenceDataEntitySelector,
  createWorkflowReferenceDataValidationIssuesSelector
} from 'selectors/workflowSelectors'
import { createParametricSelectorHook, identityPropsSelector, useParametricSelector } from 'utils'

interface WithWorkflowIdAndEntityId {
  entityId: string
  workflowId: string
}

const workflowIdSelector = <T extends WithWorkflowIdAndEntityId>(_: {}, ownProps: T) => ownProps.workflowId
const entityIdSelector = <T extends WithWorkflowIdAndEntityId>(_: {}, ownProps: T) => ownProps.entityId

export const createReferenceDataEntityHook = <TEntityType extends EntityType>(entityType: TEntityType) =>
  createParametricSelectorHook<WithWorkflowIdAndEntityId, ReferenceDataEntityType<EntityTypeFor<TEntityType>> | null>(
    createWorkflowReferenceDataEntitySelector(entityType, workflowIdSelector, entityIdSelector)
  )

const referenceDataAvailableEventsSelector = createWorkflowReferenceDataAvailableEventsSelector(identityPropsSelector)
export const useReferenceDataAvailableEvents = (workflowId: string) => {
  return useParametricSelector(referenceDataAvailableEventsSelector, workflowId)
}

const referenceDataValidationIssuesSelector = createWorkflowReferenceDataValidationIssuesSelector(identityPropsSelector)
export const useReferenceDataValidationIssues = (workflowId: string) => {
  return useParametricSelector(referenceDataValidationIssuesSelector, workflowId)
}

export const useReferenceDataEntity = <TEntityType extends EntityType>(entityType: TEntityType, workflowId: string, entityId: string) => {
  const [selector] = useState(() => createWorkflowReferenceDataEntitySelector(entityType, workflowIdSelector, entityIdSelector))
  return useParametricSelector(selector, { workflowId, entityId })
}

export const useReferenceDataEntities = <TEntityType extends EntityType>(entityType: TEntityType, workflowId: string) => {
  const [selector] = useState(() => createWorkflowReferenceDataEntitiesSelector(entityType, identityPropsSelector))
  return useParametricSelector(selector, workflowId)
}
